/**
 * Marketplace service reads and writes.
 *
 * Writes: a 2xx is a committed change, so a forward-evolved response body degrades to null rather
 * than throwing — the caller already got its effect. Reads are strict and parse per row.
 */
import { z } from 'zod';

import { logError } from '~/common/utils/log';

import {
  BuyNowResultSchema,
  ContestBrowseEnvelopeSchema,
  ContestBrowseRowSchema,
  ContestEntriesEnvelopeSchema,
  ContestEntryRowSchema,
  ContestSaleDetailSchema,
  ContestSaleSchema,
  ContestSalesEnvelopeSchema,
  RecentSalesEnvelopeSchema,
  RecentSaleSchema,
  ContestSummarySchema,
  ContestTeamsResponseSchema,
  CreateGroupInvitesRequestSchema,
  CreateGroupSaleListingRequestSchema,
  CreateListingRequestSchema,
  CreatedListingSchema,
  ExpirationConstraintsSchema,
  MarketplaceFeeTiersSchema,
  GroupEntryViewSchema,
  GroupSaleViewSchema,
  GroupInviteSchema,
  GroupInviteWithContestSchema,
  EffectiveOfferFloorsSchema,
  EntryViewSchema,
  GlobalOfferFloorLevelSchema,
  ContestOfferFloorLevelSchema,
  MyListingSchema,
  MyPortfolioContestSchema,
  MyPortfolioContestsEnvelopeSchema,
  OfferFloorsEnvelopeSchema,
  SetOfferFloorRequestSchema,
  MyOfferListItemSchema,
  MyOfferSchema,
  MyOffersEnvelopeSchema,
  MyOffersListEnvelopeSchema,
  OfferFeedEnvelopeSchema,
  OfferFeedItemSchema,
  OffersCountEnvelopeSchema,
  PaginatedListingsEnvelopeSchema,
  PaginatedMyListingsEnvelopeSchema,
  PaginatedOffersEnvelopeSchema,
  PortfolioEnvelopeSchema,
  PublicOfferSchema,
  PortfolioHoldingSchema,
  PublicListingDetailSchema,
  PublicListingSchema,
  SettlementResultSchema,
  type ContestBrowseRow,
  type ContestEntryRow,
  type ContestSale,
  type RecentSale,
  type ContestSaleDetail,
  type ContestSummary,
  type ContestTeamsResponse,
  type CreateGroupInvitesRequest,
  type CreateGroupSaleListingRequest,
  type CreateListingRequest,
  type CreatedListing,
  type ExpirationConstraints,
  type MarketplaceFeeTiers,
  type GroupEntryView,
  type GroupSaleView,
  type GroupInvite,
  type GroupInviteStatus,
  type GroupInviteWithContest,
  type BuyNowResult,
  type ContestOfferFloorLevel,
  type EffectiveOfferFloors,
  type EntryView,
  type GlobalOfferFloorLevel,
  type MyListing,
  type MyPortfolioContest,
  type MyPortfolioContestsStatus,
  type OfferFloorPlatformDefaults,
  type SetOfferFloorRequest,
  type MyOfferListItem,
  type MyOffer,
  type OfferFeedItem,
  type PortfolioHolding,
  type PublicListing,
  type PublicOffer,
  type PublicListingDetail,
  type SettlementResult,
} from './marketplace-service.schemas';
import { marketplaceServiceInstance } from './marketplace-service.service.instance';
import { summarizeZodIssues } from './zodIssues';

export const getGroupEntryViews = async (
  entryIds: readonly string[],
  signal?: AbortSignal
): Promise<readonly GroupEntryView[]> => {
  if (entryIds.length === 0) return [];

  const params = { entryIds: entryIds.join(',') };
  const response = await marketplaceServiceInstance.get<unknown>('/splits/entries/views', {
    params,
    signal,
  });

  const envelope = z.array(z.unknown()).safeParse(response.data);
  if (envelope.success === false) {
    logError(
      'getGroupEntryViews: unexpected response envelope',
      summarizeZodIssues(envelope.error)
    );
    throw envelope.error;
  }

  return envelope.data.flatMap((entry) => {
    const parsed = GroupEntryViewSchema.safeParse(entry);
    if (parsed.success === false) {
      logError(
        'getGroupEntryViews: dropped an unparseable entry view',
        summarizeZodIssues(parsed.error)
      );
      return [];
    }
    return [parsed.data];
  });
};

export type CreateGroupInvitesPayload = CreateGroupInvitesRequest;

export const createGroupInvites = async (
  payload: CreateGroupInvitesPayload
): Promise<readonly GroupInvite[] | null> => {
  const response = await marketplaceServiceInstance.post<unknown>(
    '/splits/invites',
    CreateGroupInvitesRequestSchema.parse(payload)
  );
  const parsed = GroupInviteSchema.array().safeParse(response.data);
  if (parsed.success === false) {
    logError('createGroupInvites: unexpected response body', parsed.error, response.data);
    return null;
  }
  return parsed.data;
};

export const getReceivedGroupInvites = async (
  status?: GroupInviteStatus,
  signal?: AbortSignal
): Promise<readonly GroupInviteWithContest[]> => {
  const response = await marketplaceServiceInstance.get<unknown>('/splits/invites/received', {
    params: { status },
    signal,
  });
  const envelope = z.array(z.unknown()).safeParse(response.data);
  if (envelope.success === false) {
    logError(
      'getReceivedGroupInvites: unexpected response envelope',
      envelope.error,
      response.data
    );
    throw envelope.error;
  }

  return envelope.data.flatMap((invite) => {
    const parsed = GroupInviteWithContestSchema.safeParse(invite);
    if (parsed.success === false) {
      logError('getReceivedGroupInvites: dropped an unparseable invite', parsed.error, invite);
      return [];
    }
    return [parsed.data];
  });
};

export const getGroupInvite = async (
  inviteId: string,
  signal?: AbortSignal
): Promise<GroupInviteWithContest> => {
  const response = await marketplaceServiceInstance.get<unknown>(`/splits/invites/${inviteId}`, {
    signal,
  });
  return GroupInviteWithContestSchema.parse(response.data);
};

export const acceptGroupInvite = async (inviteId: string): Promise<GroupInvite> => {
  const response = await marketplaceServiceInstance.post<unknown>(
    `/splits/invites/${inviteId}/accept`
  );
  return GroupInviteSchema.parse(response.data);
};

export const declineGroupInvite = async (inviteId: string): Promise<GroupInvite> => {
  const response = await marketplaceServiceInstance.post<unknown>(
    `/splits/invites/${inviteId}/decline`
  );
  return GroupInviteSchema.parse(response.data);
};

export const cancelEntryGroupInvites = async (
  entryId: string
): Promise<readonly GroupInvite[] | null> => {
  const response = await marketplaceServiceInstance.post<unknown>(
    `/splits/entries/${entryId}/invites/cancel`
  );
  const parsed = GroupInviteSchema.array().safeParse(response.data);
  if (parsed.success === false) {
    logError('cancelEntryGroupInvites: unexpected response body', parsed.error, response.data);
    return null;
  }
  return parsed.data;
};

export type ListingsSortBy =
  | 'price_asc'
  | 'price_desc'
  | 'created_at'
  | 'expires_at'
  | 'share_desc'
  | 'price_to_value_asc'
  | 'price_to_value_desc';

export type ListingStatusFilter =
  'active' | 'pending_settlement' | 'filled' | 'cancelled' | 'expired';

export type GetListingsParams = {
  readonly contestId?: string;
  readonly status?: ListingStatusFilter;
  readonly sortBy?: ListingsSortBy;
  readonly offset?: number;
  readonly availableTeamIds?: readonly string[];
  readonly usedTeamIds?: readonly string[];
  readonly minShareBps?: number;
  readonly maxShareBps?: number;
};

type PageMeta = { readonly offset: number; readonly limit: number; readonly total: number };

export type ListingsPage = PageMeta & {
  readonly data: readonly PublicListing[];
  // Response-level degradation facets (e.g. team_universe → rows carry empty availableTeamIds).
  readonly degraded: readonly string[];
};

// `total` is a lower bound from the discovery upstream — page with `hasMore`.
export type ContestBrowsePage = PageMeta & {
  readonly data: readonly ContestBrowseRow[];
  readonly hasMore: boolean;
  readonly degraded: readonly string[];
};

export type ContestEntriesPage = PageMeta & {
  readonly data: readonly ContestEntryRow[];
  readonly hasMore: boolean;
  readonly degraded: readonly string[];
  // Contest-wide ownership-row count for the footer; null when the sweep degraded.
  readonly totalOwnerships: number | null;
};

export type ContestSalesSortBy =
  | 'sold_at_desc'
  | 'sold_at_asc'
  | 'share_asc'
  | 'share_desc'
  | 'price_asc'
  | 'price_desc'
  | 'price_to_value_asc'
  | 'price_to_value_desc';

export type ContestSalesPage = {
  readonly data: readonly ContestSale[];
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
  readonly degraded: readonly string[];
};

export type PortfolioPage = PageMeta & {
  readonly data: readonly PortfolioHolding[];
  // Response-level degradation facets (entry_ownership → controlsPicks forced false response-wide).
  readonly degraded: readonly string[];
};

const parseRows = <S extends z.ZodTypeAny>(
  rows: readonly unknown[],
  schema: S,
  context: string
): z.infer<S>[] =>
  rows.flatMap((row) => {
    const parsed = schema.safeParse(row);
    if (parsed.success === false) {
      logError(`${context}: dropped an unparseable row`, summarizeZodIssues(parsed.error));
      return [];
    }
    return [parsed.data];
  });

export const getListings = async (
  params?: GetListingsParams,
  signal?: AbortSignal
): Promise<ListingsPage> => {
  const response = await marketplaceServiceInstance.post<unknown>(
    '/listings/search',
    params ?? {},
    { signal }
  );

  const envelope = PaginatedListingsEnvelopeSchema.safeParse(response.data);
  if (envelope.success === false) {
    logError('getListings: unexpected response envelope', summarizeZodIssues(envelope.error));
    throw envelope.error;
  }

  const { offset, limit, total } = envelope.data;
  return {
    offset,
    limit,
    total,
    // Default [] so a producer that predates the field reads as not-degraded.
    degraded: envelope.data.degraded ?? [],
    data: parseRows(envelope.data.data, PublicListingSchema, 'getListings'),
  };
};

export const getListing = async (
  listingId: string,
  signal?: AbortSignal
): Promise<PublicListingDetail> => {
  const response = await marketplaceServiceInstance.get<unknown>(`/listings/${listingId}`, {
    signal,
  });

  const parsed = PublicListingDetailSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('getListing: unexpected response body', summarizeZodIssues(parsed.error));
    throw parsed.error;
  }
  return parsed.data;
};

export const getContestsBrowse = async (
  params?: {
    readonly limit?: number;
    readonly offset?: number;
    readonly topListings?: number;
  },
  signal?: AbortSignal
): Promise<ContestBrowsePage> => {
  const response = await marketplaceServiceInstance.get<unknown>('/contests/browse', {
    params,
    signal,
  });

  const envelope = ContestBrowseEnvelopeSchema.safeParse(response.data);
  if (envelope.success === false) {
    logError('getContestsBrowse: unexpected response envelope', summarizeZodIssues(envelope.error));
    throw envelope.error;
  }

  const { offset, limit, total, hasMore, degraded } = envelope.data;
  return {
    offset,
    limit,
    total,
    hasMore,
    degraded,
    data: parseRows(envelope.data.data, ContestBrowseRowSchema, 'getContestsBrowse'),
  };
};

export const getContestEntries = async (
  contestId: string,
  params?: {
    readonly offset?: number;
    readonly availableTeamIds?: readonly string[];
    readonly usedTeamIds?: readonly string[];
  },
  signal?: AbortSignal
): Promise<ContestEntriesPage> => {
  const response = await marketplaceServiceInstance.post<unknown>(
    `/contests/${contestId}/entries/search`,
    params ?? {},
    { signal }
  );

  const envelope = ContestEntriesEnvelopeSchema.safeParse(response.data);
  if (envelope.success === false) {
    logError('getContestEntries: unexpected response envelope', summarizeZodIssues(envelope.error));
    throw envelope.error;
  }

  const { offset, limit, total, hasMore, degraded, totalOwnerships } = envelope.data;
  return {
    offset,
    limit,
    total,
    hasMore,
    degraded,
    totalOwnerships,
    data: parseRows(envelope.data.data, ContestEntryRowSchema, 'getContestEntries'),
  };
};

export const getContestSales = async (
  contestId: string,
  params?: {
    readonly limit?: number;
    readonly cursor?: string;
    readonly minShareBps?: number;
    readonly maxShareBps?: number;
    readonly sortBy?: ContestSalesSortBy;
  },
  signal?: AbortSignal
): Promise<ContestSalesPage> => {
  const response = await marketplaceServiceInstance.get<unknown>(`/contests/${contestId}/sales`, {
    params,
    signal,
  });

  const envelope = ContestSalesEnvelopeSchema.safeParse(response.data);
  if (envelope.success === false) {
    logError('getContestSales: unexpected response envelope', summarizeZodIssues(envelope.error));
    throw envelope.error;
  }

  const { nextCursor, hasMore, degraded } = envelope.data;
  return {
    nextCursor,
    hasMore,
    degraded,
    data: parseRows(envelope.data.data, ContestSaleSchema, 'getContestSales'),
  };
};

export const getContestSale = async (
  contestId: string,
  transactionId: string,
  signal?: AbortSignal
): Promise<ContestSaleDetail> => {
  const response = await marketplaceServiceInstance.get<unknown>(
    `/contests/${contestId}/sales/${transactionId}`,
    { signal }
  );

  const parsed = ContestSaleDetailSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('getContestSale: unexpected response body', summarizeZodIssues(parsed.error));
    throw parsed.error;
  }
  return parsed.data;
};

export const getRecentSales = async (
  params: { readonly limit: number },
  signal?: AbortSignal
): Promise<RecentSale[]> => {
  const response = await marketplaceServiceInstance.get<unknown>('/sales/recent', {
    params,
    signal,
  });

  const envelope = RecentSalesEnvelopeSchema.safeParse(response.data);
  if (envelope.success === false) {
    logError('getRecentSales: unexpected response envelope', summarizeZodIssues(envelope.error));
    throw envelope.error;
  }
  return parseRows(envelope.data.data, RecentSaleSchema, 'getRecentSales');
};

// GET /contests/:contestId/teams (@Public): the contest's full team universe for the pick-display
// surfaces. Envelope, not a bare array — degraded carries 'team_universe' when the universe was
// unavailable and teams is empty.
export const getContestTeams = async (
  contestId: string,
  signal?: AbortSignal
): Promise<ContestTeamsResponse> => {
  const response = await marketplaceServiceInstance.get<unknown>(`/contests/${contestId}/teams`, {
    signal,
  });

  const parsed = ContestTeamsResponseSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('getContestTeams: unexpected response body', summarizeZodIssues(parsed.error));
    throw parsed.error;
  }
  return parsed.data;
};

// GET /contests/:contestId/summary (@Public): the marketplace aggregates for the
// contest header (openListingsCount, lowestAskCents, lowestAskListingId) in the browse-row
// shape. A 404 means the contest is unknown or not marketplace-enabled — the caller renders
// the not-tradeable state — so the axios error propagates; only a shape mismatch degrades.
export const getContestSummary = async (
  contestId: string,
  signal?: AbortSignal
): Promise<ContestSummary | null> => {
  const response = await marketplaceServiceInstance.get<unknown>(`/contests/${contestId}/summary`, {
    signal,
  });
  const parsed = ContestSummarySchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('getContestSummary: unexpected response body', summarizeZodIssues(parsed.error));
    return null;
  }
  return parsed.data;
};

// GET /my-portfolio — the caller's own holdings. Self-only on the backend: userId is the
// authenticated user's own id, so no user id appears in the path.
// Server-side ownership sort; omitted leaves the backend's default entry order.
export type PortfolioSortBy = 'share_desc' | 'share_asc';
export type PortfolioEntryStatus = 'alive';

export const getMyPortfolio = async (
  params?: {
    // Comma-delimited contest ids (their union); the server filters holdings to them. At most 50.
    readonly contestIds?: string;
    readonly limit?: number;
    readonly offset?: number;
    readonly sortBy?: PortfolioSortBy;
    // Server-side entry filter; 'alive' drops eliminated entries from the page and the total.
    readonly entryStatus?: PortfolioEntryStatus;
  },
  signal?: AbortSignal
): Promise<PortfolioPage> => {
  const response = await marketplaceServiceInstance.get<unknown>('/my-portfolio', {
    params,
    signal,
  });

  const envelope = PortfolioEnvelopeSchema.safeParse(response.data);
  if (envelope.success === false) {
    logError('getMyPortfolio: unexpected response envelope', summarizeZodIssues(envelope.error));
    throw envelope.error;
  }

  const { offset, limit, total } = envelope.data;
  return {
    offset,
    limit,
    total,
    // Default [] so a producer that predates the field reads as not-degraded.
    degraded: envelope.data.degraded ?? [],
    data: parseRows(envelope.data.data, PortfolioHoldingSchema, 'getMyPortfolio'),
  };
};

// A committed purchase.
// The buyer sends the terms they saw (expectedPriceCents + expectedShareBps); a concurrent
// seller edit → 409 LISTING_TERMS_CHANGED. Both are required (the backend rejects a body
// missing either).
export const buyNowListing = async (
  listingId: string,
  expected: { expectedPriceCents: number; expectedShareBps: number }
): Promise<BuyNowResult | null> => {
  const response = await marketplaceServiceInstance.post<unknown>(
    `/listings/${listingId}/buy-now`,
    expected
  );
  const parsed = BuyNowResultSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('buyNowListing: unexpected response body', summarizeZodIssues(parsed.error));
    return null;
  }
  return parsed.data;
};

export type CreateListingOfferInput = {
  readonly listingId: string;
  readonly shareBpsRequested: number;
  readonly bidPriceCents: number;
};

// A 201 is a committed escrow hold.
export const createListingOffer = async (
  input: CreateListingOfferInput
): Promise<PublicOffer | null> => {
  const response = await marketplaceServiceInstance.post<unknown>('/offers', {
    type: 'listing',
    listingId: input.listingId,
    shareBpsRequested: input.shareBpsRequested,
    bidPriceCents: input.bidPriceCents,
  });
  const parsed = PublicOfferSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('createListingOffer: unexpected response body', summarizeZodIssues(parsed.error));
    return null;
  }
  return parsed.data;
};

export type CreateDirectOfferInput = {
  readonly entryId: string;
  // The offer's recipient — always the entry's current pick controller.
  readonly targetUserId: string;
  readonly shareBpsRequested: number;
  readonly bidPriceCents: number;
  readonly expiresAt?: string;
};

// Direct offer to an entry's pick controller (no listing).
export const createDirectOffer = async (
  input: CreateDirectOfferInput
): Promise<PublicOffer | null> => {
  const response = await marketplaceServiceInstance.post<unknown>('/offers', {
    type: 'direct',
    entryId: input.entryId,
    targetUserId: input.targetUserId,
    shareBpsRequested: input.shareBpsRequested,
    bidPriceCents: input.bidPriceCents,
    ...(input.expiresAt !== undefined ? { expiresAt: input.expiresAt } : {}),
  });
  const parsed = PublicOfferSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('createDirectOffer: unexpected response body', summarizeZodIssues(parsed.error));
    return null;
  }
  return parsed.data;
};

export type MyListingsSortBy =
  'price_asc' | 'price_desc' | 'created_at' | 'expires_at' | 'share_desc';

export type GetMyListingsParams = {
  readonly contestId?: string;
  // A single status or a union of statuses; GET /my-listings matches several via a comma-delimited
  // list (e.g. the Closed view = filled,cancelled,expired). Omitted returns every status.
  readonly status?: ListingStatusFilter | readonly ListingStatusFilter[];
  readonly sortBy?: MyListingsSortBy;
  readonly limit?: number;
  readonly offset?: number;
};

export type MyListingsPage = PageMeta & { readonly data: readonly MyListing[] };

export const getMyListings = async (
  params?: GetMyListingsParams,
  signal?: AbortSignal
): Promise<MyListingsPage> => {
  const { status, ...rest } = params ?? {};
  const response = await marketplaceServiceInstance.get<unknown>('/my-listings', {
    // Join the array form here; omit the key
    // entirely when no status filter is set (returns every status).
    params: {
      ...rest,
      ...(status !== undefined
        ? { status: Array.isArray(status) ? status.join(',') : status }
        : {}),
    },
    signal,
  });

  const envelope = PaginatedMyListingsEnvelopeSchema.safeParse(response.data);
  if (envelope.success === false) {
    logError('getMyListings: unexpected response envelope', summarizeZodIssues(envelope.error));
    throw envelope.error;
  }

  const { offset, limit, total } = envelope.data;
  return {
    offset,
    limit,
    total,
    data: parseRows(envelope.data.data, MyListingSchema, 'getMyListings'),
  };
};

// GET /entries/:id: one viewer-aware read backing the direct-offer buy panel and the
// entry-based selling panel (no per-panel side endpoints).
export const getEntryView = async (
  entryId: string,
  signal?: AbortSignal,
  targetUserId?: string
): Promise<EntryView> => {
  const response = await marketplaceServiceInstance.get<unknown>(`/entries/${entryId}`, {
    signal,
    ...(targetUserId !== undefined ? { params: { targetUserId } } : {}),
  });
  const parsed = EntryViewSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('getEntryView: unexpected response body', summarizeZodIssues(parsed.error));
    throw parsed.error;
  }
  return parsed.data;
};

export const getGroupSaleView = async (
  entryId: string,
  signal?: AbortSignal
): Promise<GroupSaleView> => {
  const response = await marketplaceServiceInstance.get<unknown>(`/group-sale/entries/${entryId}`, {
    signal,
  });
  const parsed = GroupSaleViewSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('getGroupSaleView: unexpected response body', summarizeZodIssues(parsed.error));
    throw parsed.error;
  }
  return parsed.data;
};

export const agreeToGroupSale = async (entryId: string): Promise<GroupSaleView | null> => {
  const response = await marketplaceServiceInstance.put<unknown>(
    `/group-sale/entries/${entryId}/consent`
  );
  const parsed = GroupSaleViewSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('agreeToGroupSale: unexpected response body', summarizeZodIssues(parsed.error));
    return null;
  }
  return parsed.data;
};

export const withdrawGroupSaleAgreement = async (
  entryId: string
): Promise<GroupSaleView | null> => {
  const response = await marketplaceServiceInstance.delete<unknown>(
    `/group-sale/entries/${entryId}/consent`
  );
  const parsed = GroupSaleViewSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError(
      'withdrawGroupSaleAgreement: unexpected response body',
      summarizeZodIssues(parsed.error)
    );
    return null;
  }
  return parsed.data;
};

export const createGroupSaleListing = async (
  entryId: string,
  input: CreateGroupSaleListingRequest
): Promise<PublicListing | null> => {
  const request = CreateGroupSaleListingRequestSchema.parse(input);
  const response = await marketplaceServiceInstance.post<unknown>(
    `/group-sale/entries/${entryId}/listing`,
    request
  );
  const parsed = PublicListingSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('createGroupSaleListing: unexpected response body', summarizeZodIssues(parsed.error));
    return null;
  }
  return parsed.data;
};

// GET /my-offers: the categorised, paginated Offers list. role=buyer = offers I
// made (Buy); role=target = offers on my shares (Sell).
export type MyOffersListRole = 'buyer' | 'target';
// scope=active (default) returns pending + countered; scope=all adds closed. `total` is scoped;
// `totals` stays unscoped for the section headers/badge.
export type MyOffersListScope = 'active' | 'all';

export type MyOffersListParams = {
  readonly role: MyOffersListRole;
  readonly scope?: MyOffersListScope;
  readonly contestId?: string;
  readonly entryId?: string;
  readonly limit?: number;
  readonly offset?: number;
};

export type MyOffersTotals = {
  readonly pending: number;
  readonly countered: number;
  readonly closed: number;
};

export type MyOffersListPage = PageMeta & {
  readonly data: readonly MyOfferListItem[];
  readonly totals: MyOffersTotals;
  readonly degraded: readonly string[];
};

export const getMyOffersList = async (
  params: MyOffersListParams,
  signal?: AbortSignal
): Promise<MyOffersListPage> => {
  const response = await marketplaceServiceInstance.get<unknown>('/my-offers', { params, signal });

  const parsed = MyOffersListEnvelopeSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('getMyOffersList: unexpected response envelope', summarizeZodIssues(parsed.error));
    throw parsed.error;
  }
  const { data, total, totals, limit, offset, degraded } = parsed.data;
  return {
    data: parseRows(data, MyOfferListItemSchema, 'getMyOffersList'),
    total,
    totals: { pending: totals.pending, countered: totals.countered, closed: totals.closed },
    limit,
    offset,
    degraded: degraded ?? [],
  };
};

export type OfferStatusFilter =
  'pending' | 'accepted' | 'rejected' | 'expired' | 'cancelled' | 'failed';

export type GetOffersParams = {
  // Which side of the offer the caller is (received offers = 'target').
  readonly role: 'buyer' | 'target';
  readonly listingId?: string;
  readonly entryId?: string;
  readonly status?: OfferStatusFilter;
  readonly limit?: number;
  readonly offset?: number;
};

export type OffersPage = PageMeta & {
  readonly data: readonly PublicOffer[];
  // Facets served degraded on this read; ['entry_ownership'] forces every row's
  // conveysPickControl to false, so the caller must render the outcome as unknown.
  readonly degraded: readonly string[];
};

export const getOffers = async (
  params: GetOffersParams,
  signal?: AbortSignal
): Promise<OffersPage> => {
  const response = await marketplaceServiceInstance.get<unknown>('/offers', { params, signal });

  const envelope = PaginatedOffersEnvelopeSchema.safeParse(response.data);
  if (envelope.success === false) {
    logError('getOffers: unexpected response envelope', summarizeZodIssues(envelope.error));
    throw envelope.error;
  }

  const { offset, limit, total } = envelope.data;
  return {
    offset,
    limit,
    total,
    degraded: envelope.data.degraded ?? [],
    data: parseRows(envelope.data.data, PublicOfferSchema, 'getOffers'),
  };
};

// A committed settlement — funds and shares moved.
// `skipLocationValidation` bypasses the GeoComply gate for a seller accept: the seller is
// receiving money, not making a purchase, so no location prompt.
// A buyer accepting a counter is a purchase and must keep the gate.
export const acceptOffer = async (
  offerId: string,
  { skipLocationValidation }: { skipLocationValidation?: boolean } = {}
): Promise<SettlementResult | null> => {
  const response = await marketplaceServiceInstance.patch<unknown>(
    `/offers/${offerId}/accept`,
    undefined,
    { skipLocationValidation }
  );
  const parsed = SettlementResultSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('acceptOffer: unexpected response body', summarizeZodIssues(parsed.error));
    return null;
  }
  return parsed.data;
};

export const rejectOffer = async (offerId: string): Promise<PublicOffer | null> => {
  const response = await marketplaceServiceInstance.patch<unknown>(`/offers/${offerId}/reject`);
  const parsed = PublicOfferSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('rejectOffer: unexpected response body', summarizeZodIssues(parsed.error));
    return null;
  }
  return parsed.data;
};

// Escrow released.
export const cancelOffer = async (offerId: string): Promise<PublicOffer | null> => {
  const response = await marketplaceServiceInstance.patch<unknown>(`/offers/${offerId}/cancel`);
  const parsed = PublicOfferSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('cancelOffer: unexpected response body', summarizeZodIssues(parsed.error));
    return null;
  }
  return parsed.data;
};

// A counter is in place, one level.
export const counterOffer = async (
  offerId: string,
  counterPriceCents: number,
  counterShareBps: number
): Promise<PublicOffer | null> => {
  const response = await marketplaceServiceInstance.patch<unknown>(`/offers/${offerId}/counter`, {
    counterPriceCents,
    counterShareBps,
  });
  const parsed = PublicOfferSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('counterOffer: unexpected response body', summarizeZodIssues(parsed.error));
    return null;
  }
  return parsed.data;
};

// Revert a seller counter back to the buyer's original terms
// (isCounter=false, counter fields nulled), the offer staying pending. The 200 is the full
// reverted offer.
export const withdrawCounter = async (offerId: string): Promise<PublicOffer | null> => {
  const response = await marketplaceServiceInstance.patch<unknown>(
    `/offers/${offerId}/withdraw-counter`
  );
  const parsed = PublicOfferSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('withdrawCounter: unexpected response body', summarizeZodIssues(parsed.error));
    return null;
  }
  return parsed.data;
};

// Shares released back to the holder.
export const cancelListing = async (listingId: string): Promise<PublicListing | null> => {
  const response = await marketplaceServiceInstance.patch<unknown>(`/listings/${listingId}/cancel`);
  const parsed = PublicListingSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('cancelListing: unexpected response body', summarizeZodIssues(parsed.error));
    return null;
  }
  return parsed.data;
};

// PATCH /listings/:id edits price, share, and/or expiry — at least one
// field required (the backend refuses an empty body). The 200 body is PublicListingDto (has picks),
// not a my-listings row — useUpdateListing invalidates the my-listings query rather than merging
// this result into a MyListing.
export type UpdateListingInput = {
  readonly askingPriceCents?: number;
  readonly shareBps?: number;
  readonly expiresAt?: string;
};

export const updateListing = async (
  listingId: string,
  input: UpdateListingInput
): Promise<PublicListing | null> => {
  const response = await marketplaceServiceInstance.patch<unknown>(`/listings/${listingId}`, input);
  const parsed = PublicListingSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('updateListing: unexpected response body', summarizeZodIssues(parsed.error));
    return null;
  }
  return parsed.data;
};

export type CreateListingInput = CreateListingRequest;

export const createListing = async (input: CreateListingInput): Promise<CreatedListing | null> => {
  const response = await marketplaceServiceInstance.post<unknown>(
    '/listings',
    CreateListingRequestSchema.parse(input)
  );
  const parsed = CreatedListingSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('createListing: unexpected response body', summarizeZodIssues(parsed.error));
    return null;
  }
  return parsed.data;
};

// The create/edit flows' expiry bounds plus the only shipped source of the contest
// pick deadline.
export const getExpirationConstraints = async (
  contestId: string,
  signal?: AbortSignal
): Promise<ExpirationConstraints> => {
  const response = await marketplaceServiceInstance.get<unknown>(
    `/contests/${contestId}/expiration-constraints`,
    { signal }
  );
  const parsed = ExpirationConstraintsSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError(
      'getExpirationConstraints: unexpected response body',
      summarizeZodIssues(parsed.error)
    );
    throw parsed.error;
  }
  return parsed.data;
};

export const getFeeTiers = async (signal?: AbortSignal): Promise<MarketplaceFeeTiers> => {
  const response = await marketplaceServiceInstance.get<unknown>('/fees/tiers', { signal });
  const parsed = MarketplaceFeeTiersSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('getFeeTiers: unexpected response body', summarizeZodIssues(parsed.error));
    throw parsed.error;
  }
  return parsed.data;
};

const GAMECENTER_PAGE_SIZE = 20;
const OFFER_FEED_LIMIT = 2;

export const getReceivedOffersCount = async (
  contestId: string,
  signal?: AbortSignal
): Promise<number> => {
  const response = await marketplaceServiceInstance.get<unknown>('/my-offers', {
    params: { role: 'target', contestId, offset: 0, limit: 0 },
    signal,
  });

  const parsed = OffersCountEnvelopeSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError(
      'getReceivedOffersCount: unexpected response envelope',
      summarizeZodIssues(parsed.error)
    );
    return 0;
  }

  return parsed.data.total;
};

export const getContestPortfolio = (
  contestId: string,
  offset: number,
  signal?: AbortSignal
): Promise<PortfolioPage> =>
  getMyPortfolio(
    { contestIds: contestId, offset, limit: GAMECENTER_PAGE_SIZE, entryStatus: 'alive' },
    signal
  );

export type ContestOffersPage = PageMeta & {
  readonly data: readonly MyOffer[];
  readonly totals: MyOffersTotals;
};

export const getContestOffers = async (
  contestId: string,
  offset: number,
  signal?: AbortSignal
): Promise<ContestOffersPage> => {
  const response = await marketplaceServiceInstance.get<unknown>('/my-offers', {
    params: { contestId, scope: 'actionable', offset, limit: GAMECENTER_PAGE_SIZE },
    signal,
  });

  const envelope = MyOffersEnvelopeSchema.safeParse(response.data);
  if (envelope.success === false) {
    logError('getContestOffers: unexpected response envelope', summarizeZodIssues(envelope.error));
    throw envelope.error;
  }

  const { pending, countered, closed } = envelope.data.totals;

  return {
    offset: envelope.data.offset,
    limit: envelope.data.limit,
    total: envelope.data.total,
    totals: { pending: pending ?? 0, countered: countered ?? 0, closed: closed ?? 0 },
    data: parseRows(envelope.data.data, MyOfferSchema, 'getContestOffers'),
  };
};

export const getOfferFeed = async (
  signal?: AbortSignal
): Promise<{ items: OfferFeedItem[]; total: number }> => {
  const response = await marketplaceServiceInstance.get<unknown>('/offers/feed', {
    params: { filter: 'actionable', includeDismissed: false, limit: OFFER_FEED_LIMIT },
    signal,
  });

  const envelope = OfferFeedEnvelopeSchema.safeParse(response.data);
  if (envelope.success === false) {
    logError('getOfferFeed: unexpected response envelope', summarizeZodIssues(envelope.error));
    throw envelope.error;
  }

  const items = parseRows(envelope.data.data, OfferFeedItemSchema, 'getOfferFeed');

  return { items, total: envelope.data.total ?? items.length };
};

export const dismissOfferFeedItem = async (offerId: string): Promise<void> => {
  await marketplaceServiceInstance.post(`/offers/${offerId}/dismiss`);
};

type OfferFloorsPage = {
  readonly data: readonly ContestOfferFloorLevel[];
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
  readonly global: GlobalOfferFloorLevel | null;
  readonly platformDefaults: OfferFloorPlatformDefaults;
};

export const getOfferFloors = async (
  params?: { readonly cursor?: string; readonly limit?: number },
  signal?: AbortSignal
): Promise<OfferFloorsPage> => {
  const response = await marketplaceServiceInstance.get<unknown>('/offers/preferences/floors', {
    params,
    signal,
  });

  const envelope = OfferFloorsEnvelopeSchema.safeParse(response.data);
  if (envelope.success === false) {
    logError('getOfferFloors: unexpected response envelope', summarizeZodIssues(envelope.error));
    throw envelope.error;
  }

  const { nextCursor, hasMore, meta } = envelope.data;
  return {
    nextCursor,
    hasMore,
    global: meta.global,
    platformDefaults: meta.platformDefaults,
    data: parseRows(envelope.data.data, ContestOfferFloorLevelSchema, 'getOfferFloors'),
  };
};

export const getEffectiveOfferFloors = async (
  contestId: string,
  signal?: AbortSignal
): Promise<EffectiveOfferFloors> => {
  const response = await marketplaceServiceInstance.get<unknown>(
    `/offers/preferences/floors/contests/${encodeURIComponent(contestId)}/effective`,
    { signal }
  );
  const parsed = EffectiveOfferFloorsSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('getEffectiveOfferFloors: unexpected response body', summarizeZodIssues(parsed.error));
    throw parsed.error;
  }
  return parsed.data;
};

const withExpectedRevision = (body: SetOfferFloorRequest, expectedRevision?: number) => ({
  ...SetOfferFloorRequestSchema.parse(body),
  ...(expectedRevision === undefined ? {} : { expectedRevision }),
});

const expectedRevisionParams = (expectedRevision?: number) =>
  expectedRevision === undefined ? undefined : { params: { expectedRevision } };

export const setGlobalOfferFloors = async (
  body: SetOfferFloorRequest,
  expectedRevision?: number
): Promise<GlobalOfferFloorLevel | null> => {
  const response = await marketplaceServiceInstance.put<unknown>(
    '/offers/preferences/floors/global',
    withExpectedRevision(body, expectedRevision)
  );
  const parsed = GlobalOfferFloorLevelSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('setGlobalOfferFloors: unexpected response body', summarizeZodIssues(parsed.error));
    return null;
  }
  return parsed.data;
};

export const clearGlobalOfferFloors = async (
  expectedRevision?: number
): Promise<GlobalOfferFloorLevel | null> => {
  const response = await marketplaceServiceInstance.delete<unknown>(
    '/offers/preferences/floors/global',
    expectedRevisionParams(expectedRevision)
  );
  const parsed = GlobalOfferFloorLevelSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('clearGlobalOfferFloors: unexpected response body', summarizeZodIssues(parsed.error));
    return null;
  }
  return parsed.data;
};

export const setContestOfferFloors = async (
  contestId: string,
  body: SetOfferFloorRequest,
  expectedRevision?: number
): Promise<ContestOfferFloorLevel | null> => {
  const response = await marketplaceServiceInstance.put<unknown>(
    `/offers/preferences/floors/contests/${encodeURIComponent(contestId)}`,
    withExpectedRevision(body, expectedRevision)
  );
  const parsed = ContestOfferFloorLevelSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('setContestOfferFloors: unexpected response body', summarizeZodIssues(parsed.error));
    return null;
  }
  return parsed.data;
};

export const clearContestOfferFloors = async (
  contestId: string,
  expectedRevision?: number
): Promise<ContestOfferFloorLevel | null> => {
  const response = await marketplaceServiceInstance.delete<unknown>(
    `/offers/preferences/floors/contests/${encodeURIComponent(contestId)}`,
    expectedRevisionParams(expectedRevision)
  );
  const parsed = ContestOfferFloorLevelSchema.safeParse(response.data);
  if (parsed.success === false) {
    logError('clearContestOfferFloors: unexpected response body', summarizeZodIssues(parsed.error));
    return null;
  }
  return parsed.data;
};

export type MyPortfolioContestsPage = {
  readonly data: readonly MyPortfolioContest[];
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
  readonly degraded: readonly string[];
};

export const getMyPortfolioContests = async (
  params: {
    readonly status: MyPortfolioContestsStatus;
    readonly search?: string;
    readonly cursor?: string;
    readonly limit?: number;
  },
  signal?: AbortSignal
): Promise<MyPortfolioContestsPage> => {
  const { search, ...rest } = params;
  const trimmed = search?.trim() ?? '';
  const response = await marketplaceServiceInstance.get<unknown>('/my-portfolio/contests', {
    params: { ...rest, ...(trimmed !== '' ? { search: trimmed } : {}) },
    signal,
  });

  const envelope = MyPortfolioContestsEnvelopeSchema.safeParse(response.data);
  if (envelope.success === false) {
    logError(
      'getMyPortfolioContests: unexpected response envelope',
      summarizeZodIssues(envelope.error)
    );
    throw envelope.error;
  }

  const { nextCursor, hasMore, degraded } = envelope.data;
  return {
    nextCursor,
    hasMore,
    degraded,
    data: parseRows(envelope.data.data, MyPortfolioContestSchema, 'getMyPortfolioContests'),
  };
};

const TRUNCATED_FACET = 'truncated';

const collectCursorPages = async <
  Row,
  Page extends { data: readonly Row[]; nextCursor: string | null; hasMore: boolean },
>(
  fetchPage: (cursor: string | undefined) => Promise<Page>,
  onPage?: (page: Page) => void
): Promise<{ rows: Row[]; truncated: boolean; last: Page }> => {
  const rows: Row[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | undefined;
  for (;;) {
    // eslint-disable-next-line no-await-in-loop -- each page's cursor comes from the previous response
    const page = await fetchPage(cursor);
    rows.push(...page.data);
    onPage?.(page);
    if (!page.hasMore) return { rows, truncated: false, last: page };
    if (page.nextCursor === null || page.data.length === 0 || seenCursors.has(page.nextCursor)) {
      return { rows, truncated: true, last: page };
    }
    seenCursors.add(page.nextCursor);
    cursor = page.nextCursor;
  }
};

type AllMyPortfolioContests = {
  readonly contests: readonly MyPortfolioContest[];
  readonly degraded: readonly string[];
};

export const getAllMyPortfolioContests = async (
  status: MyPortfolioContestsStatus,
  pageSize: number,
  signal?: AbortSignal
): Promise<AllMyPortfolioContests> => {
  const degraded = new Set<string>();
  const { rows, truncated } = await collectCursorPages<MyPortfolioContest, MyPortfolioContestsPage>(
    (cursor) => getMyPortfolioContests({ status, limit: pageSize, cursor }, signal),
    (page) => page.degraded.forEach((facet) => degraded.add(facet))
  );
  if (truncated) degraded.add(TRUNCATED_FACET);
  return { contests: rows, degraded: [...degraded] };
};

export type AllOfferFloors = {
  readonly global: GlobalOfferFloorLevel | null;
  readonly platformDefaults: OfferFloorPlatformDefaults;
  readonly contests: readonly ContestOfferFloorLevel[];
};

export const getAllOfferFloors = async (
  pageSize: number,
  signal?: AbortSignal
): Promise<AllOfferFloors> => {
  const { rows, truncated, last } = await collectCursorPages<
    ContestOfferFloorLevel,
    OfferFloorsPage
  >((cursor) => getOfferFloors({ limit: pageSize, cursor }, signal));
  if (truncated) throw new Error('getAllOfferFloors: pagination stopped before the last page');
  return { global: last.global, platformDefaults: last.platformDefaults, contests: rows };
};
